import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import PopNotificationsContext from '../../../../../providers/PopNotifications/PopNotifications.context';
import api from 'api';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Checkbox, Loader } from 'ncoded-component-library';
import { Field } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import ListingContext from '../../providers/Listing/Listing.context';
import ListingEditContext from '../../subrouters/EditListingSubrouter/providers/Listing/Listing.context';
import DropzoneField from 'components/DropzoneField';
import InputField from 'components/InputField';
import validations from 'validations';
import {
  LISTING_CONTACT_BROKER_IMAGE_FILE_NAME,
  LISTING_CONTACT_FIRM_LOGO_FILE_NAME,
} from '../../utils';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import useHeightAnimation from 'hooks/useHeightAnimation';
import BossModeBadge from 'components/BossModeBadge';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';
import { EDIT_ROUTE } from 'data/global';

import './ListingContactColleagues.styles.scss';
import './ListingContactColleagues.styles.responsive.scss';

type ListingContactColleaguesProps = {
  className?: string;
};

const ListingContactColleagues: React.FC<ListingContactColleaguesProps> = (
  props,
) => {
  const { className } = props;

  const [isExternalListingShownLoader, setIsExternalListingShownLoader] =
    useState(false);

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);
  const { patchListingValue } = useContext(PatchListingContext);
  const ListingCreateContextValues = useContext(ListingContext);
  const ListingEditContextValues = useContext(ListingEditContext);

  const { pathname } = useLocation();

  const isEdit = pathname.includes(EDIT_ROUTE);

  const {
    setListing,
    listing: { _id, useExternalListingContactInformation },
  } = isEdit ? ListingEditContextValues : ListingCreateContextValues;

  const [isELCOpen, setIsELCOpen] = useState(
    useExternalListingContactInformation,
  );

  const classes = classNames('listing-contact-colleagues', className);

  const [container, containerRef] = useCallbackRef<HTMLDivElement>();

  const { t } = useTranslation();

  useHeightAnimation({
    element: container,
  });

  const uploadBrokerImage: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      const { uploadedFile, listing } = await api.listings.uploadFile(
        file,
        fileName,
        _id,
        config,
      );
      setListing(listing);
      return { ...uploadedFile, id: listing?.listingContact?.brokerImage?._id };
    },
    [_id, setListing],
  );

  const deleteImage: DropzoneDelete = useCallback(
    async (file, fileName) => {
      const {
        data: { fileId, listing },
      } = await api.listings.deleteListingContact(fileName, file.id, _id);
      setListing(listing);
      return fileId;
    },
    [_id, setListing],
  );

  const uploadCompanyLogo: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      const { uploadedFile, listing } = await api.listings.uploadFile(
        file,
        fileName,
        _id,
        config,
      );
      setListing(listing);
      return { ...uploadedFile, id: listing?.listingContact?.firmLogo?._id };
    },
    [_id, setListing],
  );

  const ExternalContactFields = useMemo(
    () => (
      <>
        <div className="lcw-page__field-row">
          <Field
            name="listingContact.firstName"
            label={t('firstName')}
            component={InputField}
            validate={validations.required()}
            required
            customOnBlur={(
              e: React.FocusEvent<HTMLTextAreaElement>,
              isValid: boolean,
            ) => {
              if (isValid) {
                patchListingValue({
                  listingContact: { firstName: e.target.value },
                });
              }
            }}
          />
          <Field
            name="listingContact.lastName"
            label={t('lastName')}
            component={InputField}
            validate={validations.required()}
            required
            customOnBlur={(
              e: React.FocusEvent<HTMLTextAreaElement>,
              isValid: boolean,
            ) => {
              if (isValid) {
                patchListingValue({
                  listingContact: { lastName: e.target.value },
                });
              }
            }}
          />
        </div>

        <div className="lcw-page__field-row">
          <Field
            name="listingContact.position"
            label={t('roleOrTitle')}
            component={InputField}
            validate={validations.required()}
            required
            customOnBlur={(
              e: React.FocusEvent<HTMLTextAreaElement>,
              isValid: boolean,
            ) => {
              if (isValid) {
                patchListingValue({
                  listingContact: { position: e.target.value },
                });
              }
            }}
          />

          <Field
            name="listingContact.companyName"
            label={t('companyName')}
            component={InputField}
            validate={validations.required()}
            required
            customOnBlur={(
              e: React.FocusEvent<HTMLTextAreaElement>,
              isValid: boolean,
            ) => {
              if (isValid) {
                patchListingValue({
                  listingContact: { companyName: e.target.value },
                });
              }
            }}
          />
        </div>

        <div className="lcw-page__company-broker">
          <div>
            <Field
              accept="image/*"
              maxFiles={1}
              label={t('avatarPhoto')}
              name={LISTING_CONTACT_BROKER_IMAGE_FILE_NAME}
              component={DropzoneField}
              uploadRequest={uploadBrokerImage}
              deleteRequest={deleteImage}
            />
          </div>
          <div>
            <Field
              className="company-logo-dropzone"
              accept="image/*"
              maxFiles={1}
              label={t('companyLogo')}
              name={LISTING_CONTACT_FIRM_LOGO_FILE_NAME}
              component={DropzoneField}
              uploadRequest={uploadCompanyLogo}
              deleteRequest={deleteImage}
            />
          </div>
        </div>
      </>
    ),
    [deleteImage, patchListingValue, t, uploadBrokerImage, uploadCompanyLogo],
  );

  if (!isSuperAdmin) return null;

  return (
    <div className={classes}>
      <div className="listing-contact-colleagues__heading">
        <h3>{t('listingContacts')}</h3>
      </div>

      <div className="listing-contact-colleagues__content">
        <div className="external-contact-container">
          <div className="title">
            <p>{t('displayListingContactInformation')}</p>
            <BossModeBadge />
          </div>
          <Checkbox
            label={t('displayListingContactInformationDescription')}
            checked={isELCOpen}
            onChange={async () => {
              try {
                setIsExternalListingShownLoader(true);
                const { data } = await api.dealRoom.toggleExternalContactUsage(
                  _id,
                  !useExternalListingContactInformation,
                );
                setListing(data);
                setIsELCOpen(data.useExternalListingContactInformation);
              } catch (e) {
                popServerError(e);
              } finally {
                setIsExternalListingShownLoader(false);
              }
            }}
          />

          <div ref={containerRef}>{isELCOpen && ExternalContactFields}</div>

          {isExternalListingShownLoader && <Loader />}
        </div>
      </div>
    </div>
  );
};

export default ListingContactColleagues;
